import { GridColDef, useKeepGroupedColumnsHidden, useGridApiRef, GridRenderCellParams, GridRowId, GridRowSelectionModel, GridCellModes, GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import { HICLoading, LocalizedString, NumberField } from 'hic-components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductionAssignedBillPageFragment, ReservedResourcePhase, useCreateProductionMutation } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { keysOf } from 'src/utils/utils'
import { AssignedProductionF, BillF, ReservationF } from '../../../../types/reservation-detail.types'
import { ParentChildRowType, ProductionAssignedRow } from './productions-assigned-grid.types'
import * as React from 'react'
import BillGroupCell from './bill-group-cell'
import * as R from 'ramda'
import { getCurrentResourceOccupancy } from '../../../../utils/resource-occupancy'
import { useHicCommonDataGridProps, HicDataGridPremium } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { UpdateCoParameter } from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import { ProductionAssignedActionType } from '../productions-assigned-actions/production-assigned-actions.type'
import {} from '@fortawesome/react-fontawesome'
import { faArrowRightArrowLeft, faSplit, faSave, faXmark, faPen } from '@fortawesome/pro-regular-svg-icons'
import { AUTO, DUMMY } from '../../../../utils/bills'
import { formatResource } from '../../../../../../../../utils/resource-type'
import { GridCellModesModel, GridStartCellEditModeParams } from '@mui/x-data-grid/models/api/gridEditingApi'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { GridEventListener, useGridApiContext } from '@mui/x-data-grid'
import { Box, Skeleton, Typography } from '@mui/material'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { TFunction } from 'i18next'
import { AssignedProductionBillPage, Bill } from 'src/app/pages/bills/utils'
import { Stack, Link } from '@vapor/material'
import { ReservationDetailDrawer } from '../../../../reservation-detail-wrapper'
import { Action, DeleteAction } from 'src/app/components/data-grid-action-cell-item'
import AddNewProductForm from 'src/app/pages/booking/pos/components/add-new-product/add-new-product.form'
import { ProductSchema } from 'src/app/pages/booking/pos/components/add-new-product/add-new-product.hook'
import { compareDates } from 'src/utils/date'
import { getCheckinFromBill, getCheckoutFromBill } from 'src/app/pages/booking/bill/components/bills-list/pos-passerby-bills-list.hook'
import GuestNameLabel from 'src/app/components/customer-details/customer-name-label'

export type AssignedProductionsGridProps = {
  propertyId: string
  bills: (BillF | Bill)[]
  assignedProductions: (AssignedProductionF | AssignedProductionBillPage)[]
  reservedResourceId: string | null
  onAssignedProductionsSelectedChange: (assignedProductionIds: string[]) => void
  onAssignedProductionAction: (assignedProductionId: string[], action: ProductionAssignedActionType) => void
  onOpenDocument: (params: UpdateCoParameter) => void
  onAddNewProduction: (billId: string) => void
  onUpdateProduction: (productionId: string, updProduction: ProductionAssignedRow, vatId: string, chargeCategoryId: string, onSuccess: () => void) => Promise<void>
  readOnly?: boolean
  groupedCityTaxView: boolean
  groupedStayView: boolean
  groupedExtraView: boolean
  allDocumentsAreClosed: boolean
  loading: boolean
  isInsideBillPage?: boolean
  onOpenReservation?: (reservationId: string) => void
  rowChildrenExpanded?: boolean
  rrIdsOfCurrentReservation?: string[]
  reservation?: ReservationF
}

export default function ProductionsAssignedGrid({
  propertyId,
  bills,
  assignedProductions,
  reservedResourceId,
  onAssignedProductionsSelectedChange,
  onAssignedProductionAction,
  onOpenDocument,
  onUpdateProduction,
  readOnly,
  onAddNewProduction,
  groupedCityTaxView,
  groupedStayView,
  groupedExtraView,
  allDocumentsAreClosed,
  loading,
  isInsideBillPage,
  onOpenReservation,
  rowChildrenExpanded,
  /** Are the reserved resouce IDs that belong to this reservation */
  rrIdsOfCurrentReservation,
  reservation,
}: AssignedProductionsGridProps) {
  const { t } = useTranslation()
  const { id: tenantId, currency } = useTenant()
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()
  const apiRef = useGridApiRef()
  const [cellOnEdit, setCellOnEdit] = useState<string>()
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({})
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])
  const [linkedReservationReference, setLinkedReservationReference] = useState<{ reservationId: string; reservedResourceId: string }>()
  const [billToAddProduction, setBillToAddProduction] = useState<(BillF | Bill) | null>(null)
  const openAddNewProduct = billToAddProduction !== null
  const showError = useErrorSnackBar()

  const [createProduction, { loading: createProductionLoading }] = useCreateProductionMutation({
    onError: showError,
    refetchQueries: ['ReservationDetail'],
  })

  async function onSubmitAddNewProduct(data: ProductSchema) {
    if (!billToAddProduction) return
    const result = await createProduction({
      variables: {
        billAllocation: [
          {
            billId: billToAddProduction.id,
            quota: 1,
          },
        ],
        value: {
          amount: data.amount,
          chargeCategoryId: data.chargeCategoryId,
          description: data.description,
          currency,
          date: data.competenceDate,
          propertyId,
          tenantId,
          quantity: 1,
          total: data.amount,
          vatId: data.chargeCategory.defaultVatId,
          reservedResourceId: billToAddProduction?.reservedResourceId?.[0] ?? undefined,
        },
      },
    })

    if (!result.errors && result.data?.createProduction.id) {
      setBillToAddProduction(null)
    }
  }

  const { rows, rowsSimpleTree } = useRows({ assignedProductions, reservedResourceId, groupedCityTaxView, groupedStayView, groupedExtraView })

  const getRowChildren = useCallback(
    (rowId: GridRowId) => {
      return rows.find((r) => r.id === rowId)?.children?.map<string>((i) => i.id)
    },
    [rows],
  )

  //#region Columns

  const handleColumnsAction = (action: ProductionAssignedActionType, rowId: GridRowId) => {
    const children = getRowChildren(rowId)
    if (children && children.length > 0) {
      onAssignedProductionAction(children, action)
    } else {
      onAssignedProductionAction([rowId as string], action)
    }
  }

  const handleColumnsEdit = (rowId: GridRowId, field?: string, initialValue?: unknown) => {
    if (field) {
      const cell: GridStartCellEditModeParams = { id: rowId, field: field, initialValue: initialValue }
      apiRef.current.startCellEditMode({ ...cell })
    }
  }

  const handleColumnsCancelEdit = useCallback(
    (rowId: GridRowId) => {
      if (cellOnEdit) apiRef.current.stopCellEditMode({ id: rowId, field: cellOnEdit, ignoreModifications: true })
    },
    [cellModesModel, cellOnEdit],
  )

  const columns = useColumns(handleColumnsAction, bills, readOnly ?? false, handleColumnsEdit, handleColumnsCancelEdit, cellModesModel, rrIdsOfCurrentReservation, setLinkedReservationReference)

  //#endregion

  const formatDate = useFormatDate()
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      columns: {
        columnVisibilityModel: {
          date: false,
        },
      },
      rowGrouping: {
        model: ['billId'],
      },
      sorting: {
        sortModel: [{ field: 'date', sort: 'asc' }],
      },
    },
  })

  const onSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel): void => {
      const resultModel = getNewSelectionModel(bills, newSelectionModel, selectionModel, rows)
      setSelectionModel(resultModel)
      onAssignedProductionsSelectedChange(resultModel.filter((id) => !isGroupRow(id)).map((id) => id.toString()))
    },
    [bills, selectionModel, rows],
  )

  useEffect(() => {
    if (allDocumentsAreClosed) {
      setSelectionModel([])
      apiRef.current.setExpandedDetailPanels([])
    }
  }, [allDocumentsAreClosed])

  const isDefaultGroupExpanded = useCallback(
    (n) => {
      return isBillOpen(bills, n.groupingKey as string)
    },
    [bills],
  )

  const processRowUpdate = useCallback(
    async (newRow: ProductionAssignedRow, oldRow: ProductionAssignedRow) => {
      const row = assignedProductions.find((r) => r.id === newRow.id)

      if (row && JSON.stringify(newRow) !== JSON.stringify(oldRow)) {
        const newRowWithNewTotal = { ...newRow, total: newRow.amount * newRow.quantity ?? 1 }
        await onUpdateProduction(row.production.id, newRowWithNewTotal, row.production.vat?.id ?? '', row.production.chargeCategory?.id ?? '', () => {
          /* if(newRowWithNewTotal.quota != 1){
            showSuccess(t('pms.booking.reservation.productions_assigned.updated_split_charge'))
          } */
          return { newRowWithNewTotal }
        })
      }
      return { ...newRow }
    },
    [onUpdateProduction, assignedProductions],
  )

  const processRowUpdateError = useCallback(
    (error: unknown) => {
      showError(error)
    },
    [showError],
  )

  const handleCellEditStart: GridEventListener<'cellEditStart'> = (params) => {
    const { field } = params
    setCellOnEdit(field)
  }

  const toggleGrouping = (toggle: boolean) => {
    const ids = apiRef.current.getAllRowIds()
    for (const id of ids) {
      const currentNode = apiRef.current.getRowNode(id)
      if (currentNode?.type === 'group') apiRef.current.setRowChildrenExpansion(id, toggle)
    }
  }
  useEffect(() => {
    toggleGrouping(rowChildrenExpanded ?? false)
  }, [rowChildrenExpanded])

  useEffect(() => {
    const billIdsToUpdate = [...rowsSimpleTree.map(({ billId }) => billId)]
    if (rowChildrenExpanded) {
      setExpandedGroups(billIdsToUpdate)
    } else setExpandedGroups((e) => [...e.filter((b) => !billIdsToUpdate.includes(b))])
    billIdsToUpdate.map((b) => {
      apiRef.current.setRowChildrenExpansion(buildGroupRowId(b), rowChildrenExpanded ?? false)
    })
  }, [rowChildrenExpanded, rowsSimpleTree])

  /**
   * Calculation of grid height.
   * When the height is less than MAX_HEIGHT return the calculated value else the max value.
   */
  const calcHeight = useMemo(() => {
    const MAX_HEIGHT = 505
    const MIN_HEIGHT = 100

    const calc =
      rowsSimpleTree.reduce((acc: number, current) => {
        const progressive = acc + 51 + (rowChildrenExpanded || expandedGroups.includes(current.billId) ? current.children.length * 35 : 0)
        console.log('progressive', progressive)
        return progressive
      }, 0) + 42

    return calc < MAX_HEIGHT ? (calc < MIN_HEIGHT ? MIN_HEIGHT : calc) : MAX_HEIGHT
  }, [rowsSimpleTree, rowChildrenExpanded, expandedGroups])

  const checkinFromProduction = useMemo(() => {
    if (billToAddProduction && 'reservation' in billToAddProduction) {
      return billToAddProduction?.productions?.reduce(
        (a: ProductionAssignedBillPageFragment | null, b: ProductionAssignedBillPageFragment | null) =>
          a?.production?.reservedResource && b?.production?.reservedResource
            ? a.production.reservedResource.start < b.production.reservedResource.start
              ? a
              : b
            : a?.production?.reservedResource
            ? a
            : b?.production?.reservedResource
            ? b
            : null,
        null,
      )?.production?.reservedResource?.start
    }
  }, [billToAddProduction])

  const checkoutFromProduction = useMemo(() => {
    if (billToAddProduction && 'reservation' in billToAddProduction) {
      return billToAddProduction?.productions?.reduce(
        (a: ProductionAssignedBillPageFragment | null, b: ProductionAssignedBillPageFragment | null) =>
          a?.production?.reservedResource && b?.production?.reservedResource
            ? a.production.reservedResource.end > b.production.reservedResource.end
              ? a
              : b
            : a?.production?.reservedResource
            ? a
            : b?.production?.reservedResource
            ? b
            : null,
        null,
      )?.production?.reservedResource?.end
    }
  }, [billToAddProduction])

  const checkinFromReservation = useMemo(() => {
    if (!reservation) return null

    let checkinReservation: Date | null = null
    for (const reservedResource of reservation.reservedResources ?? []) {
      if (!checkinReservation) checkinReservation = reservedResource?.start ?? null
      else if (compareDates(checkinReservation, reservedResource.start) < 0) checkinReservation = reservedResource?.start ?? null
    }

    return checkinReservation
  }, [reservation])

  const checkoutFromReservation = useMemo(() => {
    if (!reservation) return null

    let checkoutReservation: Date | null = null
    for (const reservedResource of reservation.reservedResources ?? []) {
      if (!checkoutReservation) checkoutReservation = reservedResource?.end ?? null
      else if (compareDates(reservedResource.end, checkoutReservation) < 0) checkoutReservation = reservedResource?.end ?? null
    }

    return checkoutReservation
  }, [reservation])

  const checkinFromBillReservation = useMemo(() => {
    if (!billToAddProduction || !('reservation' in billToAddProduction)) return null

    let checkinReservation: Date | null = null
    for (const reservationBill of billToAddProduction?.reservation ?? []) {
      for (const reservedResource of reservationBill.reservedResources ?? []) {
        if (!checkinReservation) checkinReservation = reservedResource?.start ?? null
        else if (compareDates(checkinReservation, reservedResource.start) < 0) checkinReservation = reservedResource?.start ?? null
      }
    }

    return checkinReservation
  }, [billToAddProduction])

  const checkoutFromBillReservation = useMemo(() => {
    if (!billToAddProduction || !('reservation' in billToAddProduction)) return null

    let checkoutReservation: Date | null = null
    for (const reservationBill of billToAddProduction?.reservation ?? []) {
      for (const reservedResource of reservationBill.reservedResources ?? []) {
        if (!checkoutReservation) checkoutReservation = reservedResource?.end ?? null
        else if (compareDates(reservedResource.end, checkoutReservation) < 0) checkoutReservation = reservedResource?.end ?? null
      }
    }

    return checkoutReservation
  }, [billToAddProduction])

  return (
    <>
      {/* <Debugger position={'bottom-right'}>{JSON.stringify(groupChangeLoading)}</Debugger> */}
      {/* {JSON.stringify(expandedGroups)} */}
      {loading && <HICLoading />}
      <div style={{ height: `${calcHeight}px`, maxHeight: '505px' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <HicDataGridPremium //
              sx={{
                '& .MuiDataGrid-cell--editable:hover': {
                  bgcolor: (theme) => theme.palette.action.hover,
                  borderBottomColor: (theme) => theme.palette.secondary.main,
                  color: (theme) => theme.palette.secondary.main,
                },
              }}
              {...hicCommonDataGridProProps}
              apiRef={apiRef}
              columns={columns}
              rows={rows}
              initialState={initialState}
              getRowHeight={(params) => {
                if (isGroupRow(params.id)) return 'auto'
                return 35
              }}
              hideFooter
              isRowSelectable={(params) => {
                if (isGroupRow(params.id)) {
                  return isBillOpen(bills, getBillId(params.id))
                } else {
                  return !(params.row as ProductionAssignedRow).paid
                }
              }}
              onCellEditStart={handleCellEditStart}
              cellModesModel={cellModesModel}
              onCellModesModelChange={(model) => setCellModesModel(model)}
              isCellEditable={(params) => {
                const row = params.row as ProductionAssignedRow
                return !row.cityTax && !row.paid && !row.grouped
              }}
              disableRowSelectionOnClick
              rowGroupingColumnMode="single"
              checkboxSelection
              getRowClassName={(params) => {
                let className = ''
                if (isGroupRow(params.id)) {
                  const billIsOpen = isBillOpen(bills, getBillId(params.id))
                  className = `bill-group-row${billIsOpen ? '' : '-disabled'}`
                } else {
                  const assignedProductionOpen = !(params.row as ProductionAssignedRow).paid
                  className = `bill-group-item-row${assignedProductionOpen ? '' : '-disabled'}`
                }
                return className
              }}
              groupingColDef={{
                leafField: 'date',
                hideDescendantCount: true,
                colSpan: (params) => {
                  if (params.rowNode.type === 'group' && params.rowNode.isAutoGenerated) {
                    return 10
                  } else {
                    return undefined
                  }
                },
                renderCell: (params) => {
                  if (params.rowNode.type === 'group' && params.rowNode.isAutoGenerated) {
                    const bill = bills.find((b) => params.rowNode.type === 'group' && b.id === params.rowNode.groupingKey)
                    if (bill) {
                      return (
                        <BillGroupCell //
                          bill={bill}
                          apiRef={apiRef}
                          params={params}
                          onDocumentOpen={onOpenDocument}
                          onAddNewProduction={() => onAddNewProduction(bill.id)}
                          readOnly={readOnly}
                          selectedProductionAssignedIds={rows.filter((r) => selectionModel.includes(r.id) && r.billId === bill.id)?.map((item) => item.id)}
                          onAction={(action, rowId) => {
                            onAssignedProductionAction(
                              rowId.map((r) => r as string),
                              action,
                            )
                          }}
                          isInsideBillPage={isInsideBillPage}
                          onOpenReservation={onOpenReservation}
                          onExpansionEvent={(expandedId, expanded) => {
                            const billId = getBillId(expandedId)
                            if (billId) {
                              setExpandedGroups((d) => {
                                if (expanded) return [...d, billId]
                                return [...d.filter((v) => v !== billId)]
                              })
                            }
                          }}
                          onAddProduction={setBillToAddProduction}
                        />
                      )
                    } else {
                      return undefined
                    }
                  } else {
                    const { date, id } = params.row as ProductionAssignedRow
                    if (!isDummyRow(id)) return formatDate(date)
                    else return <span>{t('pms.booking.reservation.productions_assigned.no_production_items')}</span>
                  }
                },
              }}
              components={
                {
                  /**
                   * override the standard column menu. This is necessary because the menu is associated automatically to first column
                   * although in the column definition there is the property to disable it (`disableColumnMenu: true`)
                   */
                  // ColumnMenu: () => <></>,
                }
              }
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={processRowUpdateError}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={onSelectionModelChange}
              slotProps={{
                baseCheckbox: { size: 'small' },
              }}
              isGroupExpandedByDefault={isDefaultGroupExpanded}
              disableColumnMenu
            />
          </div>
        </div>
      </div>
      {openAddNewProduct && billToAddProduction !== null && (
        <AddNewProductForm
          appearance={{
            type: 'DIALOG',
            open: openAddNewProduct,
            onClose: () => {
              setBillToAddProduction(null)
            },
          }}
          onCancel={() => {
            setBillToAddProduction(null)
          }}
          onSubmit={onSubmitAddNewProduct}
          loading={createProductionLoading}
          propertyId={propertyId}
          checkout={
            'reservedResource' in billToAddProduction && billToAddProduction.reservedResource?.length
              ? getCheckoutFromBill(billToAddProduction)
              : 'reservation' in billToAddProduction && checkoutFromProduction
              ? checkoutFromProduction
              : checkoutFromBillReservation
              ? checkoutFromBillReservation
              : checkoutFromReservation
          }
          checkin={
            'reservedResource' in billToAddProduction && billToAddProduction.reservedResource?.length
              ? getCheckinFromBill(billToAddProduction)
              : 'reservation' in billToAddProduction && checkinFromProduction
              ? checkinFromProduction
              : checkinFromBillReservation
              ? checkinFromBillReservation
              : checkinFromReservation
          }
        />
      )}
      <ReservationDetailDrawer
        propertyId={propertyId}
        reservationId={linkedReservationReference?.reservationId}
        reservedResourceId={linkedReservationReference?.reservedResourceId}
        open={Boolean(linkedReservationReference)}
        onClose={() => {
          setLinkedReservationReference(undefined)
        }}
      />
    </>
  )
}

function renderRowCell(params: GridRenderCellParams<any, any, any>, renderCell: (params: GridRenderCellParams<any, any, any>) => React.ReactNode): React.ReactNode {
  if (params.rowNode.type === 'group') {
    return null
  } else {
    return renderCell(params)
  }
}

function AmountEditInputCell(props: GridRenderEditCellParams<ProductionAssignedRow, number> & { type: 'currency' | 'number' }) {
  const { id, value, field, row, type } = props
  const { currency } = row as ProductionAssignedRow
  const apiRef = useGridApiContext()
  const [localValue, setLocalValue] = useState<number | undefined>(value)

  const handleApiRef = useCallback(() => {
    if (localValue !== value) {
      apiRef.current.setEditCellValue({ id, field, value: localValue })
    } else {
      apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true })
    }
  }, [id, field, localValue, value])

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    if (['Tab', 'Enter'].includes(event.key)) {
      handleApiRef()
    }
  }

  const handleOnBlur = (event?: React.SyntheticEvent) => {
    handleApiRef()
  }

  const handleChange = (newValue: number | null) => {
    if (newValue) setLocalValue(newValue)
  }

  const amountRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (amountRef.current) {
      setTimeout(() => {
        if (amountRef.current) amountRef.current.focus()
      }, 100)
    }
  }, [value])

  switch (type) {
    case 'currency':
      return (
        <HicAmountField //
          inputRef={amountRef}
          onBlur={handleOnBlur}
          onKeyDownCapture={handleOnKeyDown}
          onChange={handleChange}
          value={localValue}
          nonNegative
          fullWidth
          currency={currency}
        />
      )
    case 'number':
      return (
        <NumberField //
          inputRef={amountRef}
          onBlur={handleOnBlur}
          onKeyDownCapture={handleOnKeyDown}
          value={localValue}
          onChange={handleChange}
          fullWidth
          inputProps={{ style: { textAlign: 'right' } }}
        />
      )
  }
}

const renderAmountEditInputCell = (params: GridRenderCellParams, type: 'number' | 'currency'): React.ReactNode => {
  return <AmountEditInputCell {...params} type={type} />
}

function useColumns(
  onAction: (action: ProductionAssignedActionType, rowId: GridRowId) => void,
  bills: (BillF | Bill)[],
  readOnly: boolean,
  edit: (rowId: GridRowId, field?: string, initialValue?: unknown) => void,
  cancelEdit: (rowId: GridRowId) => void,
  cellModesModel: GridCellModesModel,
  rrIdsOfCurrentReservation?: string[],
  setLinkedReservationReference?: (reference?: { reservationId: string; reservedResourceId: string }) => void,
): GridColDef[] {
  const { t } = useTranslation()
  const f = keysOf<ProductionAssignedRow>()
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()

  const editableSingleClickCell = (row: ProductionAssignedRow, field: string, value: string, typedValue?: any, align: 'left' | 'center' | 'right' = 'left') => {
    return (
      <Box
        sx={{ alignItems: 'center', width: '100%', height: '100%', display: 'flex', justifyContent: align === 'left' ? 'start' : align === 'right' ? 'end' : 'center' }}
        onClick={() => {
          if (!row.cityTax && !row.paid && !row.grouped) edit(row.id, field, typedValue ?? value)
        }}
      >
        {value}
      </Box>
    )
  }

  return [
    {
      field: f('billId'),
      resizable: false,
      disableColumnMenu: true,
    },
    {
      field: f('date'),
      headerName: t('pms.booking.reservation.productions_assigned.date'),
      flex: 1,
      minWidth: 80,
      maxWidth: 150,
      resizable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return renderRowCell(params, (params) => {
          const date = (params.row as ProductionAssignedRow).date
          return formatDate(date)
        })
      },
    },
    {
      field: f('chargeCategory'),
      headerName: t('pms.booking.reservation.productions_assigned.charge_category'),
      flex: 1,
      minWidth: 100,
      maxWidth: 150,
      disableColumnMenu: true,
    },
    {
      field: f('vat'),
      headerName: t('pms.booking.reservation.productions_assigned.vat'),
      flex: 1,
      minWidth: 50,
      maxWidth: 100,
      disableColumnMenu: true,
    },
    {
      field: f('displayResource'),
      headerName: t('pms.booking.reservation.productions_assigned.reserved_resource'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as ProductionAssignedRow
        if (row.reservedResourceId && row.reservationId && rrIdsOfCurrentReservation && !rrIdsOfCurrentReservation.includes(row.reservedResourceId)) {
          return (
            <Stack>
              <Link onClick={() => row.reservationId && row.reservedResourceId && setLinkedReservationReference?.({ reservationId: row.reservationId, reservedResourceId: row.reservedResourceId })}>
                {params.value}
              </Link>
              <Typography variant="captionSmall">{t('pms.booking.reservation.linked_to_other_reservation')}</Typography>
            </Stack>
          )
        } else {
          return params.value
        }
      },
    },
    {
      field: f('description'),
      headerName: t('pms.booking.reservation.productions_assigned.description'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as ProductionAssignedRow
        if (row.exonerationCode && !row.description) {
          return row.exonerationCode
        }
        const description = editableSingleClickCell(row, f('description'), row.description)
        return (
          <Stack sx={{ width: '100%' }}>
            <div title={row.description}>{description}</div>
            {row.cityTaxGuestId ? (
              <Typography variant="captionSmall">
                <GuestNameLabel guestId={row.cityTaxGuestId} renderLoading={<Skeleton animation="wave" height={10} width={'80%'} />} />
              </Typography>
            ) : null}
          </Stack>
        )
      },
      editable: true,
    },
    {
      field: f('amount'),
      headerName: t('pms.booking.reservation.productions_assigned.amount'),
      flex: 1,
      maxWidth: 100,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      type: 'number',
      renderEditCell: (params) => renderAmountEditInputCell(params, 'currency'),
      renderCell: (params) => {
        const row = params.row as ProductionAssignedRow
        const value = formatCurrency(row.amount, row.currency)
        return (
          <>
            {/* row.quota != 1 && (
              <Tooltip
                title={
                  <>
                    <div>{t('pms.booking.reservation.productions_assigned.split_charge_1')}</div>
                    <div>{t('pms.booking.reservation.productions_assigned.split_charge_2', { amount: formatCurrency(row.amount / row.quota, row.currency) })}</div>
                    <div>{t('pms.booking.reservation.productions_assigned.split_charge_3', { quota: row.quota * 100 })}</div>
                  </>
                }
              >
                <span>
                  <FontAwesomeIcon icon={faCircleInfo} />
                </span>
              </Tooltip>
            ) */}
            {editableSingleClickCell(row, f('amount'), value, params.value, 'right')}
          </>
        )
      },
      editable: true,
    },
    {
      field: f('quantity'),
      headerName: t('pms.booking.reservation.productions_assigned.quantity'),
      flex: 1,
      maxWidth: 80,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      type: 'number',
      renderEditCell: (params) => renderAmountEditInputCell(params, 'number'),
      renderCell: (params) =>
        renderRowCell(params, (params) => {
          const row = params.row as ProductionAssignedRow
          return editableSingleClickCell(row, f('quantity'), row.quantity.toString(), params.value, 'right')
        }),
      editable: true,
    },
    {
      field: f('total'),
      headerName: t('pms.booking.reservation.productions_assigned.total'),
      flex: 1,
      maxWidth: 100,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) =>
        renderRowCell(params, (params) => {
          const row = params.row as ProductionAssignedRow
          return formatCurrency(row.total, row.currency)
        }),
    },

    {
      field: 'actions',
      type: 'actions',
      minWidth: 52,
      width: 72,
      resizable: false,
      getActions: (params) => {
        const row = params.row as ProductionAssignedRow
        const disabled = !isDummyRow(row.id) && isBillOpen(bills, row.billId) ? false : true

        const editOnCurrentRow = cellModesModel[row.id] ? GridCellModes.Edit : GridCellModes.View
        if (editOnCurrentRow === GridCellModes.Edit)
          return [
            <Action disabled={disabled || readOnly} icon={faSave} label={t('commons.edit')} />,
            <Action
              disabled={disabled || readOnly}
              icon={faXmark}
              onClick={() => {
                cancelEdit(params.id)
              }}
              label={t('commons.cancel')}
            />,
          ]
        else
          return [
            <Action
              disabled={disabled || readOnly}
              icon={faArrowRightArrowLeft}
              onClick={() => {
                onAction('MOVE', params.id)
              }}
              label={t('pms.booking.reservation.productions_assigned.action.move.title')}
              showInMenu
            />,
            <DeleteAction
              disabled={disabled || readOnly}
              onClick={() => {
                onAction('DELETE', params.id)
              }}
              showInMenu
            />,
            row.cityTax ? (
              <Action
                disabled={disabled || readOnly}
                icon={faPen}
                onClick={() => {
                  onAction('EDIT_CITY_TAX', params.id)
                }}
                label={t('pms.booking.reservation.productions_assigned.action.edit_city_tax.title')}
                showInMenu
              />
            ) : (
              <Action
                disabled={disabled || readOnly}
                icon={faSplit}
                onClick={() => {
                  onAction('SPLIT', params.id)
                }}
                label={t('pms.booking.reservation.productions_assigned.action.split.title')}
                showInMenu
              />
            ),
          ]
      },
    },
  ]
}

type RO = {
  start: Date
  end: Date
  phase: ReservedResourcePhase
  reservedResourceType:
    | {
        name: {
          description: LocalizedString
        }
        code?: string | null
        customName?: LocalizedString | null
      }
    | undefined
  resource?:
    | {
        code: string
      }
    | null
    | undefined
}
type RR = {
  id: string
  resourceOccupancies: RO[]
}

function getDescriptionForGroupedRow(item: ProductionAssignedRow, t: TFunction, formatDate: (date: Date) => string) {
  let descriptionExtended = item.description
  if (item.children && item.children.length > 1) {
    const numericDate = item.children
      .map((v) => v.date.getTime())
      .sort((a, b) => {
        if (a < b) return -1
        if (b < a) return 1
        return 0
      })

    let isSequential = true

    numericDate.forEach((v, i, list) => {
      if (i < list.length - 1) {
        if (list[i + 1] - v > 60 * 60 * 24 * 1000) {
          isSequential = false
          return
        }
      }
    })

    if (isSequential) {
      const minDate = new Date(Math.min(...numericDate))
      const maxDate = new Date(Math.max(...numericDate))
      if (minDate.getTime() !== maxDate.getTime()) descriptionExtended += ` (${formatDate(minDate)} - ${formatDate(maxDate)})`
    } else {
      descriptionExtended = t('pms.booking.reservation.productions_assigned.grouped_rows')
    }
  }
  return { ...item, description: descriptionExtended }
}

function groupChargesByField({ prev, current, field }: { prev: ProductionAssignedRow[]; current: ProductionAssignedRow; field: keyof ProductionAssignedRow }): ProductionAssignedRow[] {
  const productionList = [...prev]
  const existingElement = productionList.find((item) => item[field] === current[field] && item.billId === current.billId)

  if (existingElement) {
    // Sum to last element
    existingElement.quantity += current.quantity
    existingElement.total += current.total
    existingElement.amount = existingElement.total / existingElement.quantity
    existingElement.children = [...(existingElement.children ?? []), { ...current }]
    return [...productionList]
  } else {
    // Create new group element
    const newGroupItem: ProductionAssignedRow = { ...current, children: [{ ...current }] }
    return [...productionList, newGroupItem]
  }
}

function useRows({
  assignedProductions,
  reservedResourceId,
  groupedCityTaxView,
  groupedExtraView,
  groupedStayView,
}: {
  assignedProductions: (AssignedProductionF | AssignedProductionBillPage)[]
  reservedResourceId: string | null
  groupedCityTaxView: boolean
  groupedStayView: boolean
  groupedExtraView: boolean
}): { rows: ProductionAssignedRow[]; rowsSimpleTree: ParentChildRowType[] } {
  const formatDate = useFormatDate()
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const hotelDate = useHotelDate()

  const rows = useMemo(() => {
    const resultRows = assignedProductions
      // .filter((ap) => reservedResourceId === null || ap.production.reservedResource?.id === reservedResourceId || !ap.production.reservedResource?.id) // This is a redundancy. Filter on selected reservedResource is applied outside on assignedProductions
      .map((ap) => {
        const rr = ap.production.reservedResource
        const ro = rr && getCurrentResourceOccupancy<RR, RO>(hotelDate, rr)
        const resourceDescription = ro?.reservedResourceType ? formatResource(localizeString, ro.reservedResourceType, ro.resource ?? undefined) : undefined
        const displayResource = resourceDescription ? resourceDescription : '-'
        const grouped = ap.production.chargeCategory?.cityTax ? groupedCityTaxView : ap.production.chargeCategory?.stayIncluded ? groupedStayView : groupedExtraView

        const apRow: ProductionAssignedRow = {
          id: ap.id,
          date: ap.production.date,
          chargeCategory: ap.production.chargeCategory?.name ?? '',
          chargeCategoryId: ap.production.chargeCategory?.id ?? '',
          cityTax: ap.production.chargeCategory?.cityTax ?? false,
          stayIncluded: ap.production.chargeCategory?.stayIncluded ?? false,
          vat: ap.production.vat?.code ?? '',
          displayResource,
          description: ap.production.description || '',
          exonerationCode: ap.production.cityTax?.exoneration ?? null,
          cityTaxGuestId: ap.production.cityTax?.guestId ?? null,
          currency: ap.production.currency,
          amount: ap.production.amount * ap.quota,
          quantity: ap.production.quantity,
          total: ap.production.total * ap.quota,
          quota: ap.quota,
          billId: ap.billId,
          reservedResourceId: rr?.id,
          reservationId: rr?.reservationId,
          paid: ap.paid ?? false,
          grouped,
        }
        return apRow
      })

    //#region Group by operations
    let stayRows: ProductionAssignedRow[] = []
    let cityTaxRows: ProductionAssignedRow[] = []
    let extraRows: ProductionAssignedRow[] = []

    resultRows.forEach((r) => {
      if (r.stayIncluded) stayRows.push(r)
      else if (r.cityTax) cityTaxRows.push(r)
      else extraRows.push(r)
    })

    if (groupedStayView) {
      stayRows = stayRows
        .reduce((prev: ProductionAssignedRow[], current: ProductionAssignedRow): ProductionAssignedRow[] => {
          return groupChargesByField({ prev, current, field: 'chargeCategoryId' })
        }, [])
        .map((item) => {
          return getDescriptionForGroupedRow(item, t, formatDate)
        })
    }

    if (groupedCityTaxView) {
      cityTaxRows = cityTaxRows
        .sort((a, b) => {
          // Sort by bill
          if (a.billId < b.billId) return -1
          if (a.billId > b.billId) return 1

          // Sort by cityTax
          if (a.exonerationCode && b.exonerationCode) {
            if (a.exonerationCode < b.exonerationCode) return -1
            if (a.exonerationCode > b.exonerationCode) return 1
          } else {
            if (a.exonerationCode) return -1
            if (b.exonerationCode) return 1
          }

          return 0
        })
        .reduce((prev: ProductionAssignedRow[], current: ProductionAssignedRow): ProductionAssignedRow[] => {
          return groupChargesByField({ prev, current, field: 'exonerationCode' })
        }, [])
        .map((item) => {
          return getDescriptionForGroupedRow(item, t, formatDate)
        })
    }

    if (groupedExtraView) {
      extraRows = extraRows
        .reduce((prev: ProductionAssignedRow[], current: ProductionAssignedRow): ProductionAssignedRow[] => {
          return groupChargesByField({ prev, current, field: 'chargeCategoryId' })
        }, [])
        .map((item) => {
          return { ...item, description: item.chargeCategory }
        })
    }

    //#endregion Group by operations

    return [...stayRows, ...cityTaxRows, ...extraRows]
  }, [JSON.stringify(assignedProductions), reservedResourceId, hotelDate, groupedCityTaxView, groupedStayView, groupedExtraView])

  /**
   * A simple tree structure with billIds and relative children rowsIds
   */
  const rowsSimpleTree = useMemo(() => {
    return rows.reduce((grResult: ParentChildRowType[], current) => {
      const copy = [...grResult]
      const existBill = grResult?.findIndex((v) => v.billId === current.billId) ?? -1
      if (existBill >= 0) {
        copy[existBill].children.push(current.id)
        return copy
      } else return [...grResult, { billId: current.billId, children: [current.id] }]
    }, [])
  }, [rows])

  return { rows, rowsSimpleTree }
}

function isGroupRow(id: GridRowId): boolean {
  return typeof id === 'string' && id.includes(AUTO)
}

function isDummyRow(id: GridRowId): boolean {
  return typeof id === 'string' && id.includes(DUMMY)
}

function getBillId(id: GridRowId): string | undefined {
  if (typeof id === 'string' && id.includes(AUTO)) {
    return id.split('/')[1]
  } else {
    return undefined
  }
}

function buildGroupRowId(billId: string): GridRowId {
  return `${AUTO}/${billId}`
}

function isBillOpen(bills: (BillF | Bill)[], billId: string | undefined): boolean {
  return bills.find((b) => b.id === billId)?.open === true
}

function getNewSelectionModel(bills: (BillF | Bill)[], newSelectionModel: GridRowId[], oldSelectionModel: GridRowId[], gridRows: ProductionAssignedRow[]): GridRowId[] {
  const billsUnchecked: string[] = []
  const assignedProductionUnchecked: string[] = []
  const billsChecked: string[] = []
  const assignedProductionChecked: string[] = []

  const newSelectionModelWithChildren = newSelectionModel.reduce((result: GridRowId[], current: GridRowId) => {
    if (gridRows.some((r) => r.id === current)) {
      const childrenIds = gridRows.find((r) => r.id === current)?.children?.map<GridRowId>((k) => k.id)
      if (childrenIds && childrenIds.length) return [...result, ...childrenIds]
      else return [...result, current]
    } else if (isGroupRow(current)) return [...result, current]
    return result
  }, [])

  oldSelectionModel.forEach((oldId) => {
    if (!newSelectionModelWithChildren.some((newId) => newId === oldId)) {
      if (isGroupRow(oldId)) {
        const uncheckedBillId = getBillId(oldId)
        if (uncheckedBillId) {
          billsUnchecked.push(uncheckedBillId)
        }
      } else {
        assignedProductionUnchecked.push(oldId.toString())
      }
    }
  })

  newSelectionModelWithChildren.forEach((newId) => {
    if (!oldSelectionModel.some((oldId) => oldId === newId)) {
      if (isGroupRow(newId)) {
        const checkedBill = getBillId(newId)
        if (checkedBill) {
          billsChecked.push(checkedBill)
        }
      } else {
        assignedProductionChecked.push(newId.toString())
      }
    }
  })

  let resultModel = oldSelectionModel

  assignedProductionUnchecked.forEach((apu) => {
    resultModel = resultModel.filter((id) => id !== apu)
    const apBillId = bills.find((b) => b.productions?.some((ap) => ap.id === apu))?.id
    if (apBillId) {
      const groupGridRowId = buildGroupRowId(apBillId)
      resultModel = resultModel.filter((id) => id !== groupGridRowId)
    }
  })

  billsUnchecked.forEach((bu) => {
    const groupGridRowId = buildGroupRowId(bu)
    resultModel = resultModel.filter((id) => id !== groupGridRowId)
    const billAps = bills.find((b) => b.id === bu)?.productions || []
    resultModel = resultModel.filter((id) => !billAps.some((bap) => bap.id === id))
  })

  assignedProductionChecked.forEach((apc) => {
    resultModel.push(apc)
    const apBill = bills.find((b) => b.productions?.some((ap) => ap.id === apc))
    let areSelected = false
    // check needed because in the console it was throwing an error
    // This expression is not callable. Each member of the union type ... has signatures, but none of those signatures are compatible with each other.

    if (apBill && 'owner' in apBill) {
      areSelected = apBill?.productions?.every((p) => resultModel.some((id) => id === p.id)) ?? false
    } else {
      areSelected = apBill?.productions?.every((p) => resultModel.some((id) => id === p.id)) ?? false
    }

    if (apBill && areSelected) {
      const groupGridRowId = buildGroupRowId(apBill.id)
      resultModel.push(groupGridRowId)
    }
  })

  billsChecked.forEach((bc) => {
    const groupGridRowId = buildGroupRowId(bc)
    resultModel.push(groupGridRowId)
    bills
      .find((b) => b.id === bc)
      ?.productions?.forEach((ap) => {
        resultModel.push(ap.id)
      })
  })

  resultModel = R.uniq(resultModel)
  return resultModel
}
